import type { PlantTemperatureRequirements } from '@/data/plants';

// Broad input type - covers static Plant, GeneratedPlant, and partial objects from the API
export type PlantLike = {
  type?: string;
  season?: string;
  tempRequirements?: PlantTemperatureRequirements;
} | null | undefined;

/**
 * Returns temperature requirements for any plant-like object.
 * Falls back to smart defaults based on season, then type, then a warm-season
 * absolute fallback. Better to warn someone unnecessarily than miss a frost warning.
 */
export function getEffectiveTempRequirements(plant: PlantLike): PlantTemperatureRequirements {
  // If plant has explicit temp requirements, use them
  if (plant?.tempRequirements?.minNightTempF !== undefined) {
    return plant.tempRequirements as PlantTemperatureRequirements;
  }

  // If we have a top-level season hint, use season-based defaults
  if (plant?.season) {
    switch (plant.season) {
      case 'warm':
        return {
          minSoilTempF: 60,
          minNightTempF: 50,
          frostTolerant: false,
          hardFrostTolerant: false,
          heatSensitiveAboveF: 95,
          season: 'warm',
        };
      case 'cool':
        return {
          minSoilTempF: 45,
          minNightTempF: 28,
          frostTolerant: true,
          hardFrostTolerant: false,
          heatSensitiveAboveF: 80,
          season: 'cool',
        };
      case 'perennial':
        return {
          minSoilTempF: 45,
          minNightTempF: 28,
          frostTolerant: true,
          hardFrostTolerant: false,
          heatSensitiveAboveF: undefined,
          season: 'perennial',
        };
      case 'fall-planted':
        return {
          minSoilTempF: 45,
          minNightTempF: 20,
          frostTolerant: true,
          hardFrostTolerant: true,
          heatSensitiveAboveF: undefined,
          season: 'fall-planted',
        };
    }
  }

  // If we have a type field, use type-based defaults
  if (plant?.type) {
    switch (plant.type) {
      case 'vegetable':
        // Default vegetables to warm season - better to be cautious
        return {
          minSoilTempF: 60,
          minNightTempF: 50,
          frostTolerant: false,
          hardFrostTolerant: false,
          heatSensitiveAboveF: 95,
          season: 'warm',
        };
      case 'herb':
        return {
          minSoilTempF: 50,
          minNightTempF: 32,
          frostTolerant: true,
          hardFrostTolerant: false,
          heatSensitiveAboveF: undefined,
          season: 'perennial',
        };
      case 'fruit':
        return {
          minSoilTempF: 45,
          minNightTempF: 25,
          frostTolerant: true,
          hardFrostTolerant: false,
          heatSensitiveAboveF: undefined,
          season: 'perennial',
        };
      case 'flower':
        return {
          minSoilTempF: 55,
          minNightTempF: 45,
          frostTolerant: false,
          hardFrostTolerant: false,
          heatSensitiveAboveF: 95,
          season: 'warm',
        };
      case 'tree':
      case 'shrub':
        return {
          minSoilTempF: 45,
          minNightTempF: 20,
          frostTolerant: true,
          hardFrostTolerant: true,
          heatSensitiveAboveF: undefined,
          season: 'perennial',
        };
    }
  }

  // Absolute fallback - unknown plant, assume warm season to be safe
  // Better to warn someone unnecessarily than miss a frost warning
  return {
    minSoilTempF: 55,
    minNightTempF: 45,
    frostTolerant: false,
    hardFrostTolerant: false,
    heatSensitiveAboveF: undefined,
    season: 'warm',
  };
}
